import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface Slide {
  id: number;
  url: string;
  caption: string;
}

interface HeroSectionProps {
  name: string;
  location: string;
  slides: Slide[];
}

export default function HeroSection({ name, location, slides }: HeroSectionProps) {
  const { t } = useTranslation();
  const [current, setCurrent] = useState(0);
  const [captionVisible, setCaptionVisible] = useState(true);

  useEffect(() => {
    const timer = setInterval(() => {
      setCaptionVisible(false);
      setTimeout(() => {
        setCurrent((prev) => (prev + 1) % slides.length);
        setCaptionVisible(true);
      }, 400);
    }, 6000);
    return () => clearInterval(timer);
  }, [slides.length]);

  const scrollToPhilosophy = () => {
    const el = document.getElementById('philosophy');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative w-full h-screen min-h-[600px] overflow-hidden">
      {/* Slides */}
      {slides.map((slide, i) => (
        <div
          key={slide.id}
          className={`hero-slide ${i === current ? 'active' : ''}`}
        >
          <img
            src={slide.url}
            alt={slide.caption}
            className="w-full h-full object-cover object-top"
            loading={i === 0 ? 'eager' : 'lazy'}
          />
        </div>
      ))}

      {/* Dark overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/60 z-10" />

      {/* Content */}
      <div className="absolute inset-0 z-20 flex flex-col items-center justify-center text-center px-6">
        {/* Gold line */}
        <div className="w-12 h-px bg-[#B59A6A] mb-8 opacity-90" />

        <p className="font-sans text-[11px] tracking-[0.35em] uppercase text-[#B59A6A] mb-4">
          {location}
        </p>

        <h1 className="font-serif text-5xl md:text-7xl font-medium text-white leading-tight mb-6 max-w-3xl">
          {name}
        </h1>

        <p className="font-sans text-xs tracking-[0.2em] uppercase text-white/60 mb-12">
          {t('hero.subtitle')}
        </p>

        {/* Scroll indicator */}
        <button
          onClick={scrollToPhilosophy}
          className="flex flex-col items-center gap-2 cursor-pointer group"
          aria-label={t('hero.discover')}
        >
          <span className="font-sans text-[10px] tracking-[0.3em] uppercase text-white/60 group-hover:text-[#B59A6A] transition-colors duration-300">
            {t('hero.discover')}
          </span>
          <div className="w-px h-12 bg-white/30 group-hover:bg-[#B59A6A] transition-colors duration-300 animate-pulse" />
        </button>
      </div>

      {/* Slide caption bottom-right */}
      <div className="absolute bottom-9 right-10 z-20 text-right">
        <p
          className="font-sans text-[10px] tracking-[0.3em] uppercase text-white/50 transition-all duration-500"
          style={{ opacity: captionVisible ? 1 : 0, transform: captionVisible ? 'translateY(0)' : 'translateY(6px)' }}
        >
          {slides[current]?.caption}
        </p>
      </div>

      {/* Slide indicators */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-2">
        {slides.map((_, i) => (
          <button
            key={i}
            onClick={() => setCurrent(i)}
            className={`h-px transition-all duration-500 cursor-pointer ${
              i === current ? 'w-10 bg-[#B59A6A]' : 'w-5 bg-white/40'
            }`}
            aria-label={`Slide ${i + 1}`}
          />
        ))}
      </div>
    </section>
  );
}
